"use client";

import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bell, Loader2 } from "lucide-react";
import { useSettings } from "@/hooks/use-settings";
import { usePushNotifications } from "@/hooks/use-push-notifications";
import { toast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";
import type { Settings } from "@convex/settings";

interface NotificationsSectionProps {
  settings: Settings;
}

export function NotificationsSection({ settings }: NotificationsSectionProps) {
  const { updateSettings } = useSettings();
  const { isSupported, isEnabled, isRegistering, register } = usePushNotifications();
  const [isSaving, setIsSaving] = useState(false);

  const handleToggleReminders = async (enabled: boolean) => {
    console.log("[Settings] Toggle reminders:", { enabled, isEnabled, isSupported });
    setIsSaving(true);
    try {
      // Always register when enabling to ensure we have a fresh token
      if (enabled) {
        console.log("[Settings] Calling register()...");
        const registered = await register();
        console.log("[Settings] Register result:", registered);
        if (!registered) {
          toast({
            title: "Push notifications required",
            description: "Please allow notifications to enable block reminders.",
            variant: "destructive",
          });
          setIsSaving(false);
          return;
        }
      }

      await updateSettings({ blockReminders: enabled });
      toast({
        title: enabled ? "Reminders enabled" : "Reminders disabled",
        description: enabled
          ? "You'll get a notification 15 min before each block."
          : "Block reminders are now off.",
      });
    } catch (error) {
      toast({
        title: "Failed to update settings",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isSupported) {
    return null; // Don't show on web
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Notifications</CardTitle>
        </div>
        <CardDescription>Get reminded before your blocks</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="block-reminders">Block Reminders</Label>
            <p className="text-xs text-muted-foreground">
              Get notified 15 minutes before a block starts
            </p>
          </div>
          {isSaving || isRegistering ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <Switch
              id="block-reminders"
              checked={settings.blockReminders}
              onCheckedChange={handleToggleReminders}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
